import { ReactNode } from "react";
import { useTranslations } from "@zapier/i18n";
import { Text } from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import { Connection } from "@zapier/client";

import { getConnectionMemberCount } from "../getConnectionMemberCount";
import styles from "./ConnectionsConvertToManagedModal.module.css";

interface Props {
  connection: Connection;
  shareBack: boolean;
}

const strong = (chunks: ReactNode) => (
  <Text tag="strong" type="SmallPrint1Multiline" fontWeight={600}>
    {chunks}
  </Text>
);

export function ConvertToManagedImpactList({ connection, shareBack }: Props) {
  const t = useTranslations("connections-convert-to-managed.modal");
  const zapCount = connection.zapCount ?? 0;
  const memberCount = getConnectionMemberCount(connection);
  const ownerName =
    connection.owner?.fullName ||
    connection.owner?.email ||
    t("labels.previous-owner");

  return (
    <div className={styles.section}>
      <Text type="Body3">{t("labels.what-happens")}</Text>
      <ul className={styles.checkList}>
        <CheckItem>{t("bullets.ownership")}</CheckItem>
        <CheckItem>{t.rich("bullets.credential", { strong })}</CheckItem>
        <CheckItem>
          {memberCount === undefined
            ? t.rich("bullets.zaps", { zapCount, strong })
            : t.rich("bullets.access", { memberCount, zapCount, strong })}
        </CheckItem>
        {!shareBack && (
          <CheckItem icon="alertInfo">
            {t("bullets.previous-owner-loses-access", { ownerName })}
          </CheckItem>
        )}
      </ul>
    </div>
  );
}

function CheckItem({
  children,
  icon = "formCheck",
}: {
  children: ReactNode;
  icon?: "formCheck" | "alertInfo";
}) {
  return (
    <li>
      <Icon
        name={icon}
        size={16}
        color={icon === "formCheck" ? "StatusSuccess" : "TextWeakest"}
        aria-hidden
      />
      <Text type="SmallPrint1Multiline" color="TextWeaker">
        {children}
      </Text>
    </li>
  );
}
